import { useCallback, useEffect, useState } from 'react';
import { BaseLineFeedback } from '@/app/components/icons/BaseLineFeedback';
import { CollectBtn } from '@/app/components/shared/Community';
import { EllipsisDropdownMenu } from '@/app/components/shared/EllipsisDropdownMenu';
import LikeButton from '@/app/components/shared/LikeButton';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { type ActionDialogType, CommunityEnum, EllipsisActionTypeEnum } from '@/app/types';
import { getVideoMetadata } from '@/app/utils/client';
import type { TutorialInterface } from '../types';
import { getDisplayIpLocation } from '../utils';
import { CommunityShareBtn } from './CommunityShareBtn';
import { FeedDetailMediaFrame } from './FeedDetailMediaFrame';
import { UserInfo } from './UserInfo';

export const Tutorial = ({
  tutorial,
  onVideoPlayEnd,
  handleCollect,
  handleUnCollect,
  handleVote,
  handleUnVote,
  mediaAreaRef,
}: TutorialInterface) => {
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  const handleActionDialog = (action: EllipsisActionTypeEnum) => {
    openActionDialog({
      action: action as ActionDialogType,
      id: tutorial.info?.id || '',
      // TODO: 后续需要和后端一起优化，现在 type 全是 creation
      type: 'creation',
    });
  };

  useEffect(() => {
    const fetchVideoMetadata = async () => {
      const metadata = await getVideoMetadata(tutorial.info?.creation?.uri || '', {
        timeout: 15000,
        includeDuration: false,
      });
      setAspectRatio(metadata.aspectRatio);
    };
    fetchVideoMetadata();
  }, [tutorial.info?.creation?.uri]);

  /**
   * 媒体区域
   * @returns
   */
  const renderMediaArea = useCallback(() => {
    return (
      <FeedDetailMediaFrame
        src={tutorial.info?.creation.uri || ''}
        poster={tutorial.info?.creation.cover_uri}
        aspectRatio={aspectRatio}
        exploreTab={CommunityEnum.Tutorial}
        hasAudio={true}
        defaultVolume={1}
        creationId={tutorial.info?.id || ''}
        autoPlay={true}
        mediaAreaRef={mediaAreaRef as React.RefObject<HTMLDivElement | null>}
        onPlayEnd={onVideoPlayEnd}
      />
    );
  }, [
    aspectRatio,
    mediaAreaRef,
    onVideoPlayEnd,
    tutorial.info?.creation.cover_uri,
    tutorial.info?.creation.uri,
    tutorial.info?.id,
  ]);

  /**
   * 教程信息栏
   * @returns
   */
  const renderInfo = () => {
    return (
      <>
        <div className="flex h-full w-[304px] flex-col overflow-y-auto md:w-full">
          {tutorial.info?.creator && (
            <UserInfo
              user={tutorial.info.creator}
              ipLocation={getDisplayIpLocation(tutorial.info.creator.id, tutorial.ip_location)}
            />
          )}

          <div className="flex flex-1 flex-col pt-3">
            {tutorial.info?.title && (
              <p className="pt-1 font-medium text-sm text-system-white">{tutorial.info.title}</p>
            )}
            {tutorial.info?.detail && <p className="mt-2 text-sm text-system-white64">{tutorial.info.detail}</p>}

            <span className="mt-3 text-system-white/50 text-xs">{SITE === 'china' && '内容由 AI 生成'}</span>
          </div>
        </div>
        {tutorial.info?.react_info && <div className="mt-4 w-full">{renderCommonOperations()}</div>}
      </>
    );
  };

  /**
   * 在信息栏最底部展示的通用操作按钮
   * 包括：收藏、分享、更多、点赞
   * @returns
   */
  const renderCommonOperations = () => {
    if (!tutorial.info) return null;
    const { react_info } = tutorial.info;

    return (
      <div className="flex h-10 items-center gap-2 md:h-12 md:gap-3">
        <CollectBtn
          key={tutorial.info?.id}
          id={tutorial.info?.id || ''}
          tabType={CommunityEnum.Tutorial}
          itemCollect={react_info?.collected ?? false}
          itemCollectCount={react_info?.collect_count ?? 0}
          hideEmptyCollectCount={false}
          hideCount={true}
          handleCollect={handleCollect}
          handleUnCollect={handleUnCollect}
          className="flex aspect-square size-10 h-full cursor-pointer items-center justify-center rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12"
          iconClassName="size-5 md:size-6"
        />
        <CommunityShareBtn id={tutorial.info?.id || ''} type={CommunityEnum.Tutorial} />
        {SITE === 'china' && (
          <EllipsisDropdownMenu
            className="aspect-square size-10 h-full rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12 md:text-2xl"
            actions={[{ action: EllipsisActionTypeEnum.InspirationReport, icon: <BaseLineFeedback /> }]}
            onSelectedAction={handleActionDialog}
          />
        )}
        <LikeButton
          className="h-full flex-1 rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12"
          id={tutorial.info.id}
          itemVote={react_info?.voted || false}
          tabType={CommunityEnum.Tutorial}
          itemVoteCount={react_info?.vote_count || 0}
          traceId={tutorial.traceId || ''}
          handleVote={handleVote}
          handleUnVote={handleUnVote}
        />
      </div>
    );
  };

  return (
    <>
      <div
        className="absolute inset-0 z-[-1] bg-black/60 bg-center bg-cover bg-no-repeat blur-[150px] md:hidden"
        style={{
          backgroundImage: `url('${tutorial.info?.creation.cover_uri}')`,
        }}
      >
        <div className="absolute inset-0 z-[-1] size-full bg-black/60" />
      </div>

      <div className="relative flex size-full flex-1 md:flex-col md:overflow-y-auto md:overflow-x-hidden">
        {renderMediaArea()}
        {/* 右侧信息栏 */}
        <div className="relative my-8 flex flex-col border-system-white10 border-l pr-[30px] pl-5 md:my-0 md:flex-1 md:border-0 md:px-4 md:pb-6">
          {renderInfo()}
        </div>
      </div>
    </>
  );
};
